// =====================================================
// BROKER REGISTRATION VALIDATION UTILITIES
// =====================================================

import { BrokerFormData, Specialization, License } from '@/types/broker-registration.types';

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * Validate email format
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate Step 1: Profile
 */
export function validateStepOne(data: BrokerFormData): ValidationResult {
  const errors: Record<string, string> = {};

  // Basic information
  if (!data.firstName?.trim()) {
    errors.firstName = 'First name is required';
  }
  if (!data.lastName?.trim()) {
    errors.lastName = 'Last name is required';
  }
  if (!data.email?.trim()) {
    errors.email = 'Email is required';
  } else if (!validateEmail(data.email)) {
    errors.email = 'Invalid email format';
  }
  if (!data.phone.trim()) {
    errors.phone = 'Phone is required';
  }
  
  // Password validation
  if (!data.password) {
    errors.password = 'Password is required';
  } else if (data.password.length < 8) {
    errors.password = 'Password must be at least 8 characters';
  }
  if (!data.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password';
  } else if (data.password !== data.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  // Address
  if (!data.addressType) {
    errors.addressType = 'Please select an address type';
  }
  if (!data.streetAddress.trim()) {
    errors.streetAddress = 'Street address is required';
  }
  if (!data.city.trim()) {
    errors.city = 'City is required';
  }
  if (!data.stateProvince.trim()) {
    errors.stateProvince = 'State/Province is required';
  }
  if (!data.zipPostalCode.trim()) {
    errors.zipPostalCode = 'Zip/Postal code is required';
  }
  if (!data.country) {
    errors.country = 'Country is required';
  }

  // Languages
  if (!data.motherTongue) {
    errors.motherTongue = 'Mother tongue is required';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate specializations
 */
function validateSpecializations(specializations: Specialization[]): Record<string, string> {
  const errors: Record<string, string> = {};

  specializations.forEach((spec, index) => {
    if (!spec.industry) {
      errors[`specialization_${index}_industry`] = 'Please select an industry';
    }
    if (spec.expertises.length === 0) {
      errors[`specialization_${index}_expertises`] = 'Please select at least one expertise';
    }
    if (!spec.experienceYears) {
      errors[`specialization_${index}_experienceYears`] = 'Please select experience years';
    }
  });

  return errors;
}

/**
 * Validate licenses
 */
function validateLicenses(licenses: License[]): Record<string, string> {
  const errors: Record<string, string> = {};

  licenses.forEach((license, index) => {
    if (!license.details?.trim()) {
      errors[`license_${index}_details`] = 'License details are required';
    }
    if (!license.number?.trim()) {
      errors[`license_${index}_number`] = 'License number is required';
    }
  });

  return errors;
}

/**
 * Validate Step 2: Expertise & Licenses
 */
export function validateStepTwo(data: BrokerFormData): ValidationResult {
  const errors: Record<string, string> = {};

  // Validate specializations
  const specializationErrors = validateSpecializations(data.specializations);
  Object.assign(errors, specializationErrors);

  // Validate licenses
  const licenseErrors = validateLicenses(data.licenses);
  Object.assign(errors, licenseErrors);

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

// All required NDA enforcement values
const NDA_REQUIRED_VALUES = [
  'prefinancing_share',
  'standardized_documents',
  'positive_cashflow_only',
  'acquizgroup_evaluation',
];

/**
 * Validate Step 3: Transaction Model
 */
export function validateStepThree(data: BrokerFormData): ValidationResult {
  const errors: Record<string, string> = {};

  if (data.transactionTypes.length === 0) {
    errors.transactionTypes = 'Please select at least one transaction type';
  }
  
  // ALL NDA enforcement options must be checked
  const allNdaSelected = NDA_REQUIRED_VALUES.every(v => data.ndaEnforcement.includes(v));
  if (!allNdaSelected) {
    errors.ndaEnforcement = 'You must agree to all NDA enforcement policies to continue';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate Step 4: Methodology (KPIs)
 */
export function validateStepFour(data: BrokerFormData): ValidationResult {
  const errors: Record<string, string> = {};

  if (data.postSaleRole.length === 0) {
    errors.postSaleRole = 'Please select at least one post-sale role option';
  }
  if (data.vettingProcess.length === 0) {
    errors.vettingProcess = 'Please select at least one PoF requirement';
  }

  // Conditional validation for PoF sub-options
  if (data.vettingProcess.includes('cash_down_payment') && !data.cashDownMinPercent) {
    errors.cashDownMinPercent = 'Please select the down payment percentage';
  }
  if (data.vettingProcess.includes('fixed_liquid_amount')) {
    if (!data.fixedAmountMin) {
      errors.fixedAmountMin = 'Minimum amount is required';
    }
    if (!data.fixedAmountMax) {
      errors.fixedAmountMax = 'Maximum amount is required';
    }
  }

  if (data.acceptedPofTypes.length === 0) {
    errors.acceptedPofTypes = 'Please select at least one PoF document type';
  }
  if (data.competingOffers.length === 0) {
    errors.competingOffers = 'Please select at least one option for competing offers';
  }
  if (!data.dealSizeMin) {
    errors.dealSizeMin = 'Please select minimum deal size';
  }
  if (!data.dealSizeMax) {
    errors.dealSizeMax = 'Please select maximum deal size';
  }
  if (data.feeStructure.length === 0) {
    errors.feeStructure = 'Please select at least one fee structure option';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate Step 5: Compliance
 */
export function validateStepFive(data: BrokerFormData): ValidationResult {
  const errors: Record<string, string> = {};

  if (!data.termsAccepted) {
    errors.termsAccepted = 'You must accept the terms and conditions';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate a specific step
 */
export function validateStep(step: number, data: BrokerFormData): ValidationResult {
  switch (step) {
    case 1:
      return validateStepOne(data);
    case 2:
      return validateStepTwo(data);
    case 3:
      return validateStepThree(data);
    case 4:
      return validateStepFour(data);
    case 5:
      return validateStepFive(data);
    default:
      return { isValid: true, errors: {} };
  }
}

/**
 * Scroll to the first error element
 */
export function scrollToFirstError(): void {
  const firstError = document.querySelector('.border-red-500');
  if (firstError) {
    firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}
